import React, { useState } from 'react';
import { sortBy, } from 'lodash';
import { toast } from 'react-toastify';
import { Button, Input } from 'reactstrap';
import copy from 'copy-to-clipboard';
import uuid from 'uuid';
import { format as formatDate, addDays } from 'date-fns';
import DatePicker from 'react-datepicker';

import { functions } from '../firebase';
import useDocumentSubscription from './hooks/useDocumentSubscription';
import ModalButton from './ModalButton';

const setPublicPagePassword = functions.httpsCallable('setPublicPagePassword');
const minPasswordLength = 4;

export default function ShareButton ({ label, screenType, company, subjectType, subjects, showsExpires = false, ...extraProps }) {
  const [selectedDate, selectDate] = useState(addDays(new Date(), 14));
  const [newPassword, setNewPassword] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const sortedSubjectIds = sortBy(subjects.map(_ => _?.id), _ => _);
  const budgetPublicKeyId = sortedSubjectIds.join('__');
  const budgetPublicKey = useDocumentSubscription(sortedSubjectIds.join('').length > 0 && company.ref.collection('budgetPublicKeys').doc(budgetPublicKeyId), [budgetPublicKeyId], { strict: true });
  const url = `${window.location.origin}/p/${screenType}?subjectType=${subjectType}&publicKey=${budgetPublicKey?.publicKey}`;
  const content = () => {
    const onClickCopy = () => {
      copy(url);
      toast.success('コピーしました');
    };
    const onClickShare = async () => {
      if(!window.confirm('本当に共有URLを作成しますか？')) return;

      await company.ref.collection('budgetPublicKeys').doc(budgetPublicKeyId).set({
        subjectType,
        publicKey: uuid(),
        publicExpiredOn: selectedDate,
        subjectIds: sortedSubjectIds,
      });
    };
    const onClickUnshare = async () => {
      if(!window.confirm('共有URLを削除しますか？')) return;

      await company.ref.collection('budgetPublicKeys').doc(budgetPublicKeyId).delete();
    };
    const onClickSavePassword = async () => {
      setIsSavingPassword(true);
      try {
        await setPublicPagePassword({ companyId: company.id, budgetPublicKeyId, password: newPassword, });
        setNewPassword('');
        toast.success('パスワードを設定しました');
      } catch(e) {
        console.error(e);
        toast.error('保存に失敗しました');
      }
      setIsSavingPassword(false);
    };
    const onClickRemovePassword = async () => {
      if(!window.confirm('パスワードを解除しますか？URLを知っていれば誰でも閲覧できるようになります。')) return;

      setIsSavingPassword(true);
      try {
        await setPublicPagePassword({ companyId: company.id, budgetPublicKeyId, password: null, });
        toast.success('パスワードを解除しました');
      } catch(e) {
        console.error(e);
        toast.error('解除に失敗しました');
      }
      setIsSavingPassword(false);
    };

    return budgetPublicKey != null ? (
      <div>
        <div className="d-flex gap-1">
          <Input className="flex-grow-1" style={{ minWidth: 0 }} readOnly defaultValue={url} />
          <Button color="primary" onClick={onClickCopy}>
            <span className="fas fa-copy" />
          </Button>
          <Button color="danger" onClick={onClickUnshare}>
            <span className="fas fa-times" />
          </Button>
        </div>
        <div className="d-flex text-muted small">
          {budgetPublicKey.publicExpiredOn != null && `共有期限: ${formatDate(budgetPublicKey.publicExpiredOn.toDate(), 'YYYY/MM/DD')}`}
        </div>
        <hr />
        <div className="small text-muted mb-1">
          パスワード
          {budgetPublicKey.passwordHash ? (
            <span className="ml-1 badge badge-success">設定済み</span>
          ) : (
            <span className="ml-1 badge badge-secondary">未設定</span>
          )}
        </div>
        <div className="d-flex gap-1">
          <Input
            className="flex-grow-1"
            style={{ minWidth: 0 }}
            type="password"
            value={newPassword}
            onChange={_ => setNewPassword(_.target.value)}
          />
          <Button
            className="text-nowrap"
            color="primary"
            onClick={onClickSavePassword}
            disabled={newPassword.length < minPasswordLength || isSavingPassword}
          >
            {budgetPublicKey.passwordHash ? '変更' : '設定'}
          </Button>
          {
            budgetPublicKey.passwordHash && (
              <Button className="text-nowrap" color="danger" outline onClick={onClickRemovePassword} disabled={isSavingPassword}>
                解除
              </Button>
            )
          }
        </div>
      </div>
    ) : (
      <div className="d-flex flex-column align-items-center gap-2">
        <div>
          <div className="small text-muted">共有期限</div>
          <DatePicker
            dateFormat="yyyy/MM/dd"
            selected={selectedDate}
            onChange={selectDate}
          />
        </div>
        <Button color="primary" onClick={onClickShare} disabled={selectedDate == null}>
          共有URLを作成する
        </Button>
      </div>
    );
  };

  return !company.disablesBudgetsShare && (
    <div>
      <ModalButton title="共有" {...extraProps} content={content}>
        <span className="fas fa-share-alt" />
        {label && <span className="ml-1">{label}</span>}
      </ModalButton>
      {
        showsExpires && budgetPublicKey != null && (
          <div className="text-muted small">
            {formatDate(budgetPublicKey.publicExpiredOn.toDate(), 'YYYY/MM/DD')}
          </div>
        )
      }
    </div>
  );
};
